import React from "react";
import { Stack, Card, rem, useLang } from "@manulife/mux";
import { useFormFocus } from "../../customHooks/useFormFocus";
import RtgInputs from "../RtgInputs/RtgInputs";
import RtgResult from "../RtgResult/RtgResult";
import { useDispatch } from "react-redux";
import { fetchReport } from "../../appRedux/report/Actions";
import { getAmountNumbers } from "../../services/helpers/commonHelpers";

const RtgFormSection = ({
  cardWidth,
  num20,
  num40,
  handleSubmit,
  setFocus,
  fields
}) => {
  const dispatch = useDispatch();
  const selectedLang = useLang();
  const { focusField } = useFormFocus();

  const fetchCalculation = (data) => {
    const formattedData = formatCalculationData(data, selectedLang);
    dispatch(fetchReport(formattedData));
  };

  const handleError = (errors) => {
    const fieldsOrder = [...fields.mount]; // Get the order of fields
    const firstErrorField = fieldsOrder.find((field) => field.includes("rtgSynChilds") ? isValid(errors["rtgSynChilds"], field) : errors[field]);

    handleFieldError(firstErrorField, errors, focusField, setFocus);
  };

  const isValid = (errors, field) => {
    // eslint-disable-next-line no-unused-vars
    const [_, index, fieldName] = field.split("."); // Destructure the parts of the field string
    return errors?.[index]?.[fieldName];
  }

  return (
    <Stack direction="row" gap={rem(num40)} align="flex-start" justify="space-evenly">
      <Stack
        as="form"
        onSubmit={handleSubmit(fetchCalculation, handleError)}
        gap={rem(num20)}
        align="flex-start"
        justify="flex-start"
        style={{ ...cardWidth }}
      >
        <RtgInputs />
      </Stack>
      <Card
        customStyle={{
          cardStyle: { ...cardWidth, alignSelf: "stretch" },
        }}
        data-testid="resultColumn"
      >
        <RtgResult />
      </Card>
    </Stack>
  );
};

const formatCalculationData = (data, selectedLang) => {
  const {
    rtgSelectedProduct,
    rtgCostType,
    rtgAmount,
    rtgAge,
    rtgSex,
    rtgHealthStyle,
    rtgSynClientName,
    rtgSynTermRiderAmount,
    rtgSynChildProtectionRiders,
    rtgSynChildCount,
    rtgSynChilds,
  } = data;

  return {
    product: rtgSelectedProduct,
    costType: rtgCostType,
    amount: getAmountNumbers(rtgAmount, selectedLang),
    age: rtgAge,
    gender: rtgSex,
    healthStyle: rtgHealthStyle,
    clientName: rtgSynClientName,
    termRiderAmount: getAmountNumbers(rtgSynTermRiderAmount || 0, selectedLang),
    childProtectionRiders: rtgSynChildProtectionRiders,
    childCount: rtgSynChildCount,
    childs: rtgSynChilds,
    selectedLang,
  };
};

const handleFieldError = (errorField, errors, focusField, setFocus) => {
  switch (errorField.split(".")[0]) { // required for rtgSynChilds
    case "rtgSex":
    case "rtgHealthStyle":
    case "rtgSynChildProtectionRiders":
      focusField(errorField);
      break;
    case "rtgSynChilds":
      handleChildrenErrors(errors["rtgSynChilds"], focusField, setFocus);
      break;
    default:
      if (errorField) {
        const fullFieldPath = `${errorField}`;
        try {
          setFocus(fullFieldPath);
        } catch (e) { }
      }
  }
};

const handleChildrenErrors = (errors, focusField, setFocus) => {
  const childFields = {
    AGE: "age",
    SEX: "sex",
    INSURED_TYPES: "insuredTypes",
    CRITICAL_ILLNESS_AMOUNT: "criticalIllnessAmount"
  };

  const firstNonNullIndex = errors.findIndex(
    (error) => error !== null && Object.keys(error || {}).length > 0
  );

  const errorField = Object.keys(childFields)
    .map((key) => childFields[key])
    .find(
      (field) =>
        errors[firstNonNullIndex] &&
        Object.prototype.hasOwnProperty.call(errors[firstNonNullIndex], field)
    );

  switch (errorField) {
    case childFields.SEX:
    case childFields.INSURED_TYPES:
      focusField("childRefs", firstNonNullIndex, errorField);
      break;
    default:
      if (errorField) {
        const fullFieldPath = `rtgSynChilds.${firstNonNullIndex}.${errorField}`;
        try {
          setFocus(fullFieldPath);
        } catch (e) { }
      }
  }
};

export default RtgFormSection;